import React from "react";
import { Star, PlaneTakeoff, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const HERO_IMAGE =
  "https://commons.wikimedia.org/wiki/Special:FilePath/Registan%20Samarkand%20Uzbekistan.JPG?width=1600";

function Avatar({ initial, z }) {
  return (
    <div
      className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-700 ring-2 ring-slate-950 flex items-center justify-center text-xs font-semibold text-white -ml-3 first:ml-0"
      style={{ zIndex: z }}
    >
      {initial}
    </div>
  );
}

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[100svh] sm:min-h-[94vh] overflow-hidden bg-slate-950">
      <img
        src={HERO_IMAGE}
        alt="Регистан, Самарканд"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-950/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />

      <div className="relative z-10 flex flex-col justify-end min-h-[100svh] sm:min-h-[94vh] px-6 sm:px-8 md:px-14 pb-64 sm:pb-40 md:pb-32 pt-24 sm:pt-28 max-w-5xl">
        <span className="inline-flex w-fit items-center gap-2 text-[10px] sm:text-xs font-bold tracking-[0.2em] sm:tracking-[0.25em] text-amber-200 border border-amber-300/30 bg-amber-400/10 rounded-full px-4 py-1.5 sm:px-5 sm:py-2 mb-6 sm:mb-8 uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          {t("home_hero.badge", "TRAVEL ACROSS UZBEKISTAN")}
        </span>

        {/* Заголовок с убранным "that" */}
        <h1 className="font-serif text-white text-4xl sm:text-5xl md:text-7xl leading-[1.05] md:leading-[1.02] mb-4 sm:mb-6 tracking-tight">
          {t("home_hero.title_main", "Восток, который")}
          <br />
          <em className="italic font-light text-amber-300">
            {t("home_hero.title_highlight", "остаётся")}
          </em>{" "}
          {t("home_hero.title_end", "с вами")}
        </h1>

        <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 max-w-md">
          {t(
            "home_hero.subtitle",
            "Авторские путешествия по древним городам, местной кухне и местам, которыми хочется делиться."
          )}
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <Link to="/cities">
            <button className="flex items-center justify-center gap-2 bg-amber-400 text-slate-950 font-bold rounded-full px-6 py-3.5 hover:bg-amber-300 transition-colors w-full sm:w-auto">
              {t("home_hero.btn_choose", "Выбрать направление")}
              <span aria-hidden>→</span>
            </button>
          </Link>

          {/* Используем Link вместо <a> для перехода без перезагрузки */}
          <Link
            to="/how-it-works"
            className="flex items-center justify-center sm:justify-start gap-1.5 text-amber-300 font-semibold hover:text-amber-200 transition-colors py-2 sm:py-0"
          >
            {t("home_hero.btn_how_it_works", "Как это работает")}
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-dashed border-white/20 bg-slate-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 sm:py-6 flex flex-col sm:flex-row items-center sm:justify-between gap-4 sm:gap-6">
          <div className="flex items-center gap-4 w-full sm:w-auto justify-center sm:justify-start">
            <div className="flex">
              <Avatar initial="A" z={3} />
              <Avatar initial="M" z={2} />
              <Avatar initial="K" z={1} />
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-white font-bold text-base sm:text-lg leading-tight">
                4.9
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              </p>
              <p className="text-slate-400 text-xs sm:text-sm">
                {t("home_hero.rated_by", "rated by travelers")}
              </p>
            </div>
          </div>

          <div className="w-full border-t border-dashed border-white/20 sm:hidden" />
          <div className="hidden sm:block h-10 border-l border-dashed border-white/20" />

          <div className="flex items-center gap-3 text-white">
            <div className="text-right">
              <p className="text-base sm:text-lg font-bold leading-tight">TAS</p>
              <p className="text-slate-400 text-xs">
                {t("home_hero.tashkent", "Tashkent")}
              </p>
            </div>
            <div className="flex items-center gap-1 text-amber-300">
              <span className="w-6 sm:w-8 border-t border-dashed border-amber-300/60" />
              <PlaneTakeoff className="w-4 h-4" strokeWidth={2.25} />
              <span className="w-6 sm:w-8 border-t border-dashed border-amber-300/60" />
            </div>
            <div>
              <p className="text-base sm:text-lg font-bold leading-tight">SKD</p>
              <p className="text-slate-400 text-xs">
                {t("home_hero.samarkand", "Samarkand")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
